import * as React from 'react';
import {Helmet} from 'react-helmet';
import useI18n from 'src/hooks/useI18n';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {usePageTitle} from 'src/contexts/page-title-context';
import {TitleMedium} from '@spaced-out/ui-design-system/lib/components/Text';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {PageName} from '@spaced-out/ui-design-system/lib/components/PageTitle';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {
  Toast,
  toastApi,
  ToastTitle,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {duotoneIconStyle} from 'src/utils/app-shell';
import {useByodEnabled} from 'src/hooks/product-flags';
import usePrivilege from 'src/hooks/usePrivilege';
import {useDashboardList} from 'src/components/analytics-genesis/byod/api/queries/useDashboardList';
import {HomeDashboardSelector} from 'src/components/dashboard/home-dashboard-selector';
import {HomeBYODViewer} from 'src/components/dashboard/home-byod-viewer';
import Dashboard from './dashboard';
import css from './dashboard.css';


type DashboardHomeProps = React.ComponentProps<typeof Dashboard>;

export default function DashboardHome(props: DashboardHomeProps) {
  const {t} = useLabelTranslation();
  const {labelI18nInstance} = useI18n();
  const language = labelI18nInstance.language;
  const {
    actions: {setPageTitleProps},
  } = usePageTitle();

  const isByodEnabled = useByodEnabled();
  const canViewScout = usePrivilege('VIEW_REPORTS_AND_DASHBOARDS');
  const canCreateScout = usePrivilege('CREATE_REPORTS_AND_DASHBOARDS');
  const canDeleteScout = usePrivilege('DELETE_REPORTS_AND_DASHBOARDS');
  const canViewByod =
    isByodEnabled && (canViewScout || canCreateScout || canDeleteScout);
  const {
    data: listData,
    isLoading: isListLoading,
    isError: isListError,
  } = useDashboardList({
    page: 1,
    pageSize: 100,
    enabled: canViewByod,
  });
  const dashboards = listData?.dashboards ?? [];
  const homeDashboardId = React.useMemo(
    () => dashboards.find((d) => d.is_default)?.id ?? null,
    [dashboards],
  );

  const [selectedDashboardId, setSelectedDashboardId] = React.useState<
    string | null
  >(null);
  const [hasInitialized, setHasInitialized] = React.useState(false);
  // Track whether the user has explicitly picked a dashboard via the dropdown.
  // When they have, we stop syncing from the server's is_default flag.
  const userOverrodeRef = React.useRef(false);

  React.useEffect(() => {
    if (listData && !hasInitialized) {
      setSelectedDashboardId(homeDashboardId);
      setHasInitialized(true);
    }
  }, [listData, homeDashboardId, hasInitialized]);

  // Re-sync when the server's default changes (e.g. set from dashboard-list)
  // but only if the user hasn't manually switched via the dropdown.
  const prevHomeDashboardIdRef = React.useRef(homeDashboardId);
  React.useEffect(() => {
    if (
      hasInitialized &&
      homeDashboardId !== prevHomeDashboardIdRef.current &&
      !userOverrodeRef.current
    ) {
      setSelectedDashboardId(homeDashboardId);
    }
    prevHomeDashboardIdRef.current = homeDashboardId;
  }, [hasInitialized, homeDashboardId]);

  const handleDashboardChange = React.useCallback((id: string | null) => {
    userOverrodeRef.current = true;
    setSelectedDashboardId(id);
  }, []);

  const handleByodError = React.useCallback(() => {
    setSelectedDashboardId(null);
    toastApi.show(
      <Toast initialFocus={0} semantic="information">
        <ToastTitle>
          {t(
            'Dashboard_unavailable_Showing_default',
            'Dashboard unavailable. Showing default.',
          )}
        </ToastTitle>
      </Toast>,
    );
  }, [t]);

  // Set page title when showing BYOD viewer (Dashboard sets its own when rendered)
  React.useEffect(() => {
    if (selectedDashboardId != null) {
      setPageTitleProps({
        customPageNameComponent: (
          <PageName>
            <TitleMedium>{t('Dashboard', 'Dashboard')}</TitleMedium>
            <Icon type="duotone" name="house" style={duotoneIconStyle} />
          </PageName>
        ),
        showBackButton: false,
      });
    }
  }, [selectedDashboardId, language, setPageTitleProps, t]);

  if (!canViewByod || isListError) {
    return <Dashboard {...props} />;
  }

  // Wait for the dashboard list before rendering anything so we show the
  // correct dashboard from the start instead of flashing Sense Default first.
  if (isListLoading || !hasInitialized) {
    return (
      <div className={css.byodLoading}>
        <CircularLoader size="large" />
      </div>
    );
  }

  if (selectedDashboardId != null) {
    return (
      <div className={css.byodWrapper}>
        {/* @ts-expect-error - TS2786 - 'Helmet' cannot be used as a JSX component. */}
        <Helmet title={t('Dashboard', 'Dashboard')} />
        <div className={css.selectorRow}>
          <HomeDashboardSelector
            selectedDashboardId={selectedDashboardId}
            onDashboardChange={handleDashboardChange}
            dashboards={dashboards}
            homeDashboardId={homeDashboardId}
          />
        </div>
        <div className={css.byodContent}>
          <HomeBYODViewer
            dashboardId={selectedDashboardId}
            onError={handleByodError}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      {/* @ts-expect-error - TS2786 - 'Helmet' cannot be used as a JSX component. */}
      <Helmet title={t('Dashboard', 'Dashboard')} />
      <div className={css.selectorRow}>
        <HomeDashboardSelector
          selectedDashboardId={selectedDashboardId}
          onDashboardChange={handleDashboardChange}
          dashboards={dashboards}
          homeDashboardId={homeDashboardId}
        />
      </div>
      <Dashboard {...props} />
    </>
  );
}
